import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const NotificationContext = createContext(null);
const STORAGE_KEY = 'ft_notifications';
const MAX = 100;

const supportsBrowserNotif = typeof window !== 'undefined' && 'Notification' in window;

function showBrowserNotification(message) {
  if (!supportsBrowserNotif || Notification.permission !== 'granted') return;
  try {
    // Chèn giờ gửi (HH:MM) để biết thông báo phát lúc nào.
    const hhmm = new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });
    const n = new Notification('FunnyTask', {
      body: `${message} · ${hhmm}`,
      icon: '/icon-192.png',
      badge: '/badge-96.png',
      tag: 'ft_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch (e) {}
}

export function NotificationProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  });
  const [permission, setPermission] = useState(supportsBrowserNotif ? Notification.permission : 'unsupported');

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX)));
    } catch (e) {}
  }, [items]);

  // Xin quyền hiển thị thông báo trên trình duyệt (1 lần, TỪ thao tác người dùng — bắt buộc trên iOS).
  // Sau khi cấp quyền -> ĐĂNG KÝ Web Push (tạo subscription gửi về server) để nhận cả khi đóng app.
  const enableBrowserNotifications = useCallback(async () => {
    if (!supportsBrowserNotif) return 'unsupported';
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p === 'granted') {
      showBrowserNotification('Đã bật thông báo trên trình duyệt.');
      import('../push').then((m) => m.registerPush()).catch(() => {});
    }
    return p;
  }, []);

  const addNotification = useCallback((message, type = 'info') => {
    if (!message) return;
    setItems((prev) =>
      [{ id: Date.now() + '_' + Math.random().toString(36).slice(2, 6), message, type, at: Date.now(), read: false }, ...prev].slice(0, MAX)
    );
    // Thông báo OS do WEB PUSH (service worker) lo — luôn hiện kể cả khi mở app; không bắn
    // new Notification in-app nữa để tránh HIỆN 2 LẦN (và new Notification hay bị chặn khi tab focus).
  }, []);

  // Nhận thông báo realtime từ server (giao việc, đề xuất, lương…) do ChatContext phát qua window event.
  useEffect(() => {
    const onNotify = (e) => {
      const d = e.detail || {};
      const msg = [d.title, d.body].filter(Boolean).join(' — ');
      if (msg) addNotification(msg, d.type || 'info');
    };
    window.addEventListener('ft:notify', onNotify);
    return () => window.removeEventListener('ft:notify', onNotify);
  }, [addNotification]);

  const markAllRead = useCallback(() => setItems((prev) => prev.map((n) => ({ ...n, read: true }))), []);
  const clearAll = useCallback(() => setItems([]), []);
  const removeOne = useCallback((id) => setItems((prev) => prev.filter((n) => n.id !== id)), []);

  const unread = items.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{ items, addNotification, markAllRead, clearAll, removeOne, unread, permission, enableBrowserNotifications }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
